import { faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import {
  Action,
  ButtonGroup,
  Card,
  DataTable,
  DataTableStatus,
  FilterBar,
  Modal,
  Page,
  RelativeDateTime,
} from 'corso-admin-ui';
import { CrewMerchantApi, CrewMerchantUi } from 'corso-types';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useReducer, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import MetricCard from '~/components/MetricCard';
import PageStatus from '~/components/PageStatus';
import { usePathParams } from '~/hooks/usePathParams';
import { formatter } from 'corso-web-lib';
import {
  DeliveryBadges,
  DeliveryDetailSection,
  JsonCodeBlock,
} from './deliveryDetail';
import {
  deliveryFilterDefinition,
  toRequestFilters,
  type DeliveryFilter,
  type DeliveryFilterId,
  type DeliveryFilterValues,
} from './deliveryFilters';
import {
  useWebhook,
  useWebhookDeliveryRetry,
  useWebhookDeliverySearch,
} from './hooks/useWebhooks';

type Delivery = CrewMerchantUi.ConfigWebhookDelivery;
type Cursor = CrewMerchantUi.ConfigWebhookDeliverySearchReq['cursor'];

type State = {
  filters: DeliveryFilterValues;
  cursor: Cursor;
};

type DeliveryAction =
  | { type: 'filterChange'; filter: DeliveryFilter }
  | { type: 'removeFilters'; ids: DeliveryFilterId[] }
  | { type: 'clearFilters' }
  | { type: 'setCursor'; cursor: Cursor };

const initialState = { filters: {}, cursor: null } satisfies State;

const reducer = (state: State, action: DeliveryAction): State =>
  match(action)
    // any filter change invalidates the cursor — it points into the old result set
    .with({ type: 'filterChange' }, ({ filter }) => ({
      filters: { ...state.filters, [filter.id]: filter.value },
      cursor: null,
    }))
    .with({ type: 'removeFilters' }, ({ ids }) => {
      const removed = new Set<string>(ids);
      return {
        filters: Object.fromEntries(
          Object.entries(state.filters).filter(([id]) => !removed.has(id)),
        ),
        cursor: null,
      };
    })
    .with({ type: 'clearFilters' }, () => initialState)
    .with({ type: 'setCursor' }, ({ cursor }) => ({ ...state, cursor }))
    .exhaustive();

const columnHelper = createColumnHelper<Delivery>();

const useColumns = (
  onRetry: (deliveryId: number) => void,
  isRetrying: boolean,
) =>
  useMemo(
    () => [
      columnHelper.accessor('topic', {
        header: 'Topic',
        cell: (info) => info.getValue() ?? '—',
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <DeliveryBadges
            status={row.original.status}
            responseStatus={row.original.responseStatus}
          />
        ),
      }),
      columnHelper.accessor('attempts', {
        header: 'Attempts',
        cell: (info) => {
          const attempts = info.getValue();
          return `${attempts} ${formatter.plural(attempts, {
            one: 'attempt',
            other: 'attempts',
          })}`;
        },
      }),
      columnHelper.accessor('createdOn', {
        header: 'Created',
        cell: (info) => <RelativeDateTime dateTime={info.getValue()} />,
      }),
      columnHelper.accessor('lastAttemptOn', {
        header: 'Last attempt',
        cell: (info) => {
          const value = info.getValue();
          return value ? <RelativeDateTime dateTime={value} /> : '—';
        },
      }),
      columnHelper.display({
        id: 'retry',
        header: '',
        cell: ({ row }) =>
          row.original.canRetry ? (
            <Action
              variant="ghost"
              icon="undo"
              accessibilityLabel={`Retry delivery ${row.original.id}`}
              disabled={isRetrying}
              onClick={(event) => {
                event.stopPropagation();
                onRetry(row.original.id);
              }}
            />
          ) : null,
      }),
    ],
    [onRetry, isRetrying],
  );

function DeliveryDetailModal({
  delivery,
  onClose,
}: {
  delivery: Delivery;
  onClose: () => void;
}) {
  return (
    <Modal
      show
      onClose={onClose}
      title={delivery.topic ?? 'Delivery'}
      description={`Attempt ${delivery.attempts} · ${delivery.status}`}
    >
      <div className="space-y-3">
        {delivery.lastAttemptOn && (
          <p className="text-xs text-neutral-500">
            Last attempt{' '}
            {formatter.relativeTimeFromNow(new Date(delivery.lastAttemptOn))}
          </p>
        )}

        {delivery.payload != null && (
          <DeliveryDetailSection title="Request Payload">
            <JsonCodeBlock content={delivery.payload} />
          </DeliveryDetailSection>
        )}

        {delivery.responseBody != null && (
          <DeliveryDetailSection title="Response Body">
            <JsonCodeBlock
              content={delivery.responseBody}
              maxHeight="max-h-48"
            />
          </DeliveryDetailSection>
        )}

        {delivery.responseContentType != null && (
          <DeliveryDetailSection title="Response Content Type">
            <p className="text-xs text-neutral-500">
              {delivery.responseContentType}
            </p>
          </DeliveryDetailSection>
        )}
      </div>
    </Modal>
  );
}

function DeliveryMetrics({
  statusCounts,
}: {
  statusCounts: CrewMerchantUi.ConfigWebhookDeliverySearchResp['statusCounts'];
}) {
  const { Delivered: delivered, Failed: failed } = statusCounts;
  const inFlight = statusCounts.Pending + statusCounts.Processing;

  // in-flight deliveries have no outcome yet, so they are not part of the rate
  const settled = delivered + failed;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <MetricCard
        icon="webhook"
        label="Success Rate"
        // pre-formatted: MetricCard sends raw numbers through formatter.count
        value={settled === 0 ? null : formatter.percent(delivered / settled)}
      />
      <MetricCard icon="success" label="Delivered" value={delivered} />
      <MetricCard icon={faCircleXmark} label="Failed" value={failed} />
      <MetricCard icon="pending" label="In Flight" value={inFlight} />
    </div>
  );
}

type PageInfo = CrewMerchantUi.ConfigWebhookDeliverySearchResp['pageInfo'];

function DeliveryResults({
  data,
  columns,
  pageInfo,
  isError,
  isFetching,
  onRetryFetch,
  onClearFilters,
  onSelect,
  onPage,
}: {
  data: Delivery[];
  columns: ReturnType<typeof useColumns>;
  pageInfo: PageInfo;
  isError: boolean;
  isFetching: boolean;
  onRetryFetch: () => void;
  onClearFilters: () => void;
  onSelect: (delivery: Delivery) => void;
  onPage: (cursor: Cursor) => void;
}) {
  if (isError) {
    return (
      <DataTableStatus
        title="Something went wrong"
        details="We encountered an error while fetching deliveries."
        icon="error"
        action={{ label: 'Retry', onClick: onRetryFetch }}
      />
    );
  }

  if (data.length === 0) {
    // the initial fetch renders nothing rather than flashing an empty state
    if (isFetching) return null;

    return (
      <DataTableStatus
        title="No deliveries found"
        icon="search"
        details="Try widening the date range or clearing the filters."
        action={{ label: 'View all deliveries', onClick: onClearFilters }}
      />
    );
  }

  const { nextCursor, prevCursor } = pageInfo;

  return (
    <>
      <DataTable<Delivery>
        data={data}
        columns={columns}
        onRowClick={onSelect}
      />
      {(nextCursor !== null || prevCursor !== null) && (
        <div className="flex justify-center bg-neutral-100 p-2">
          <ButtonGroup horizontal>
            <Action
              icon="chevronLeft"
              accessibilityLabel="Previous page"
              disabled={prevCursor === null}
              onClick={() =>
                prevCursor !== null &&
                onPage({ direction: 'prev', id: prevCursor })
              }
            />
            <Action
              icon="chevronRight"
              accessibilityLabel="Next page"
              disabled={nextCursor === null}
              onClick={() =>
                nextCursor !== null &&
                onPage({ direction: 'next', id: nextCursor })
              }
            />
          </ButtonGroup>
        </div>
      )}
    </>
  );
}

export default function WebhookDeliveriesPage() {
  const { webhookId } = usePathParams(
    z.object({ webhookId: z.coerce.number() }),
  );
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selected, setSelected] = useState<Delivery | null>(null);

  const {
    data: webhook,
    isPending: isWebhookPending,
    isError: isWebhookError,
    error: webhookError,
  } = useWebhook(webhookId);

  const request = useMemo(
    () =>
      ({
        filters: toRequestFilters(state.filters),
        cursor: state.cursor,
        orderBy: 'desc',
        take: 25,
      }) satisfies CrewMerchantApi.RequestBody<
        '/:storeId/settings/webhooks/:webhookId/deliveries/search',
        'post'
      >,
    [state.filters, state.cursor],
  );

  const { mutate: retry, isPending: isRetrying } =
    useWebhookDeliveryRetry(webhookId);

  const { data, pageInfo, statusCounts, isFetching, isError, refetch } =
    useWebhookDeliverySearch(webhookId, request);

  const columns = useColumns(retry, isRetrying);

  if (isWebhookError) return <PageStatus.Error error={webhookError} />;
  if (isWebhookPending) return <PageStatus.Loading />;

  return (
    <Page
      backAction={{ accessibilityLabel: 'Back', to: '..' }}
      title={webhook.name}
      subtitle={<p>{webhook.endpointUrl}</p>}
    >
      <div className="flex flex-col gap-4">
        {statusCounts && <DeliveryMetrics statusCounts={statusCounts} />}

        <Card flush>
          <div className="flex flex-col gap-0 divide-y divide-neutral-200">
            <div className="p-3">
              <FilterBar
                filters={deliveryFilterDefinition}
                activeFilters={state.filters}
                onChange={(filter) =>
                  dispatch({ type: 'filterChange', filter })
                }
                onRemoveFilters={(ids) =>
                  dispatch({ type: 'removeFilters', ids })
                }
              />
            </div>

            <DeliveryResults
              data={data}
              columns={columns}
              pageInfo={pageInfo}
              isError={isError}
              isFetching={isFetching}
              onRetryFetch={() => {
                // oxlint-disable-next-line @typescript-eslint/no-floating-promises
                refetch();
              }}
              onClearFilters={() => dispatch({ type: 'clearFilters' })}
              onSelect={setSelected}
              onPage={(cursor) => dispatch({ type: 'setCursor', cursor })}
            />
          </div>
        </Card>
      </div>

      {selected && (
        <DeliveryDetailModal
          delivery={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </Page>
  );
}
